/**
 * The Review run's `result` — the statement checker's claims and verdicts.
 *
 * `SolutionRunDetailOut.result` is an untyped object in OpenAPI (its shape
 * depends on the run's kind), so this mirrors the backend Pydantic model
 * `ReviewResult` in `backend/src/daquma/services/review_checker.py`, which
 * validates every stored result. Keep the two in step.
 */

import type { Citation } from "@/features/solutions/shared";

export type Verdict = "supported" | "contradicted" | "not_found";
export type ClaimKind = "statement" | "number" | "date" | "dose" | "population" | "other";

export interface DraftLocation {
  paragraph_id: string;
  paragraph: number;
  page: number | null;
  sheet: string | null;
  rows: number[] | null;
  lines: number[] | null;
  /** e.g. `p.3, para 2` */
  label: string;
}

export interface ReviewEvidence {
  citation: Citation;
  quote: string;
}

export interface ReviewClaim {
  id: string;
  text: string;
  kind: ClaimKind;
  draft_location: DraftLocation;
  verdict: Verdict;
  explanation: string;
  evidence: ReviewEvidence[];
  draft_value: string | null;
  source_value: string | null;
  model_verdict: Verdict | null;
  downgraded: boolean;
  downgrade_reason: string | null;
  error: string | null;
}

export interface ReviewResult {
  draft: {
    source: "upload" | "document";
    display_name: string;
    index_id: string | null;
    document_id: string | null;
  };
  summary: {
    total: number;
    supported: number;
    contradicted: number;
    not_found: number;
    downgraded: number;
  };
  claims: ReviewClaim[];
  claims_extracted: number;
  claims_truncated: boolean;
}

export const VERDICT_LABEL: Record<Verdict, string> = {
  supported: "Supported",
  contradicted: "Contradicted",
  not_found: "Not found",
};

export const VERDICT_COLOR: Record<Verdict, string> = {
  supported: "green",
  contradicted: "red",
  not_found: "gray",
};

/** Contradictions first (they need action), then not found, then supported. */
const ORDER: Record<Verdict, number> = { contradicted: 0, not_found: 1, supported: 2 };

export function sortClaims(claims: ReviewClaim[]): ReviewClaim[] {
  return claims
    .map((c, i) => ({ c, i }))
    .sort((a, b) => ORDER[a.c.verdict] - ORDER[b.c.verdict] || a.i - b.i)
    .map(({ c }) => c);
}

/** Narrow a run's untyped result to a Review result (or null). */
export function asReviewResult(result: unknown): ReviewResult | null {
  if (!result || typeof result !== "object") return null;
  const r = result as Partial<ReviewResult>;
  return Array.isArray(r.claims) && r.summary ? (r as ReviewResult) : null;
}
